/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {safeDetectChanges} from '../common/util/safe-detect-changes';
import template from './site-group-analysis-page.component.html';
import {SiteResource} from '../../components/api/resources/resource/site.resource';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {visiblePageNumbers} from '../common/directives/pagination/visible-page-numbers';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

interface AnalysisListColumn {
  param: string;
  label?: string;
  labelInstant?: string;
  numeric?: boolean;
  sortable?: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-site-group-analysis-page',
  template
})
export class SiteGroupAnalysisPageComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  activeTab: 'sites' | 'reports' = 'sites';
  visibleReports = false;
  canSeeReports = false;

  sites: any = {};
  /** Snapshot — list-helper replaces sites.items on settle (NG0100). */
  displayItems: any[] = [];
  hasItems = false;
  listPageSize = 25;
  listTotalCount = 0;
  listCurrentPage = 1;
  readonly siteItemStateName = 'app.inv.sites.site';

  readonly siteColumns: AnalysisListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: 'siteUrl', labelInstant: 'Site URL'}
  ];

  private lastLoadedModelId: string | undefined;
  private subscribedModelComms: any;
  private destroyed = false;

  constructor(
    @Inject(SiteResource) private Site: any,
    private adnListHelper: AdnListHelperService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
    if (changes.model || changes.isNew) {
      this.refreshSitesIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  selectTab(tab: 'sites' | 'reports'): void {
    this.activeTab = tab;
    if (tab === 'reports') {
      this.visibleReports = true;
    }
  }

  isColumnSortable(column: AnalysisListColumn): boolean {
    return column.sortable !== false && !!column.param;
  }

  columnLabel(column: AnalysisListColumn): string {
    if (column.labelInstant) {
      return column.labelInstant;
    }
    const key = column.label || column.param;
    return key ? 'label.' + key : '';
  }

  sortBy(column: AnalysisListColumn): void {
    if (!this.isColumnSortable(column) || !this.sites?.makeSortQuery) {
      return;
    }
    if (column.numeric) {
      this.sites.makeSortQuery(column.param, column.numeric);
    } else {
      this.sites.makeSortQuery(column.param);
    }
  }

  changePage(page: number): void {
    if (!this.sites || page < 1 || page > this.totalPages() || page === this.listCurrentPage) {
      return;
    }
    this.sites.currentPage = page;
    this.sites.changePage();
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.listTotalCount / (this.listPageSize || 1)));
  }

  pageNumbers(): number[] {
    return visiblePageNumbers(this.listCurrentPage, this.totalPages());
  }

  pageRangeStart(): number {
    return this.listPageSize * (this.listCurrentPage - 1) + 1;
  }

  pageRangeEnd(): number {
    return Math.min(this.listPageSize * this.listCurrentPage, this.listTotalCount);
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    // Route isNew never flips after save; modelComms does.
    this.modelComms.addSubscriber((data: any, type: any, isNewFromComms?: boolean) => {
      if (this.destroyed || type) {
        return;
      }
      this.ngZone.run(() => {
        if (isNewFromComms === true || isNewFromComms === false) {
          this.effectiveIsNew = isNewFromComms;
        } else if (data?.name) {
          this.effectiveIsNew = false;
        }
        if (data?.name && this.model) {
          this.model.name = data.name;
        }
        if (data?.id && this.model) {
          this.model.id = data.id;
        }
        this.refreshSitesIfNeeded();
        safeDetectChanges(this.cdr);
      });
    });
  }

  private refreshSitesIfNeeded(): void {
    const modelId = this.effectiveIsNew ? undefined : this.model?.id;
    if (modelId === this.lastLoadedModelId) {
      return;
    }
    this.lastLoadedModelId = modelId;
    this.initSitesList();
  }

  private initSitesList(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.sites = {};
      this.displayItems = [];
      this.hasItems = false;
      this.listPageSize = 25;
      this.listTotalCount = 0;
      this.listCurrentPage = 1;
      return;
    }

    this.sites = {};
    this.displayItems = [];
    this.hasItems = false;
    this.listPageSize = 25;
    this.listTotalCount = 0;
    this.listCurrentPage = 1;
    this.adnListHelper.setUpBasicList(this.sites, this.Site, 'fullSitesForSiteGroup', {
      moreParams: {siteGroup: this.model.id},
      afterFill: () => {
        deferMarkForCheck(this.cdr, () => {
          this.displayItems = (this.sites.items || []).slice();
          this.hasItems = this.displayItems.length > 0;
          this.listPageSize = this.sites.pageSize || 25;
          this.listTotalCount = this.sites.totalCount || 0;
          this.listCurrentPage = this.sites.currentPage || 1;
        });
      }
    });
    this.ngZone.run(() => {
      this.sites.makeQuery?.();
    });
  }
}
